import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Editable from '../../components/Editable';
import MediaInput from '../../components/MediaInput';
import { 
    addUnit, 
    normalizeAppUrl, 
    stripHtml,
    getFontStyles,
    resolveMediaUrl,
    resolveImageFilter
} from '../../utils/styleHelpers';
import { 
    getProductImageUrl, 
    cleanHtmlDescription, 
    getProductDisplayPrice,
    slugify,
    getVariationPriceDetails,
    findMatchingVariation,
    extractProductAttributes
} from '../../utils/helpers';
import { useCurrency } from '../CurrencyState';
import { getGraphicBorderStyle, getGraphicBorderPadding } from '../graphicBorders.js';

export const ShopProductCard = ({
    product,
    idx = 0,
    isEditing = false,
    isMobile = false,
    catalogViewMode = 'grid',
    styleSettings = {},
    settings = {},
    typography = {},
    primaryColor = '#3b82f6',
    tableQuantities = {},
    handleTableQtyChange,
    addToCart,
    updateProductField,
    handleDuplicateProductLive,
    handleDeleteProductLive,
    onEditProductDetails,
    update,
    _getFontStyles,
    dbCategories = [],
    setActiveCategory,
    setActiveSubCategory,
    isCommercialMode = false,
    isSimpleMode = false,
    selectedSimpleSection = null,
    onSectionSelect,
    getSelectableAttributes,
    getEffectiveSelectedOptions,
    getMatchedVariation,
    handleOptionChange,
    tableSelectedOptions = {},
    formatPrice
}) => {
    const { formatPrice: contextFormatPrice } = useCurrency();
    const activeFormatPrice = formatPrice || contextFormatPrice;

    const resolveFontStyles = typeof _getFontStyles === 'function' 
        ? _getFontStyles 
        : getFontStyles(typography || settings?.typography || {});
    const navigate = useNavigate();

    const pId = product?.id ?? product?.rawId ?? product?._id;

    const selectableAttrs = useMemo(() => {
        if (getSelectableAttributes) return getSelectableAttributes(product);
        const allAttrs = extractProductAttributes(product);
        const selectable = {};
        Object.entries(allAttrs).forEach(([k, opts]) => {
            if (Array.isArray(opts) && opts.length > 1) {
                selectable[k] = opts;
            }
        });
        return selectable;
    }, [getSelectableAttributes, product]);

    const hasSelectableOptions = Object.keys(selectableAttrs).length > 0;
    const effectiveOptions = useMemo(() => {
        if (getEffectiveSelectedOptions) {
            return getEffectiveSelectedOptions(product);
        }
        const result = {};
        const userSelected = (pId !== undefined ? (tableSelectedOptions[pId] || tableSelectedOptions[String(pId)]) : null) || {};
        Object.entries(selectableAttrs).forEach(([attrName, optionsList]) => {
            if (userSelected[attrName] !== undefined) {
                result[attrName] = userSelected[attrName];
            } else if (optionsList && optionsList.length > 0) {
                result[attrName] = optionsList[0];
            }
        });
        return result;
    }, [getEffectiveSelectedOptions, product, tableSelectedOptions, pId, selectableAttrs]);

    const matchedVar = useMemo(() => {
        if (getMatchedVariation) {
            return getMatchedVariation(product, effectiveOptions);
        }
        return findMatchingVariation(product, effectiveOptions);
    }, [getMatchedVariation, product, effectiveOptions]);

    const priceInfo = useMemo(() => {
        return getVariationPriceDetails(matchedVar, product);
    }, [matchedVar, product]);

    const matchedImage = matchedVar?.image ? (typeof matchedVar.image === 'object' ? (matchedVar.image.src || matchedVar.image.url) : matchedVar.image) : null;
    const imageSrc = (matchedImage ? resolveMediaUrl(matchedImage) : null) || getProductImageUrl(product);

    const cardStock = matchedVar?.stock !== undefined 
        ? parseInt(matchedVar.stock, 10) 
        : ((product?.stock !== undefined && product?.stock !== null && product?.stock !== '') ? parseInt(product.stock, 10) : 999);
    const cardDirection = catalogViewMode === 'list' 
        ? 'row'
        : (isMobile
            ? (styleSettings.shopCardMobileDirection || 'column')
            : (styleSettings.shopCardDirection || 'column'));
    const isHorizontal = cardDirection === 'row' || cardDirection === 'row-reverse';
    const cardHeight = (isMobile && styleSettings.shopCardMobileHeight)
        ? styleSettings.shopCardMobileHeight
        : (isHorizontal 
            ? (styleSettings.shopCardHorizontalHeight || styleSettings.shopCardHeight || 'auto')
            : (styleSettings.shopCardHeight || 'auto'));
    const cardMinHeight = (isMobile && styleSettings.shopCardMobileMinHeight)
        ? styleSettings.shopCardMobileMinHeight
        : (isHorizontal
            ? (styleSettings.shopCardHorizontalMinHeight || styleSettings.shopCardMinHeight || '')
            : (styleSettings.shopCardMinHeight || ''));
    const cardMaxHeight = (isMobile && styleSettings.shopCardMobileMaxHeight)
        ? styleSettings.shopCardMobileMaxHeight
        : (isHorizontal
            ? (styleSettings.shopCardHorizontalMaxHeight || styleSettings.shopCardMaxHeight || '')
            : (styleSettings.shopCardMaxHeight || ''));

    const activeImageAspectRatio = (isMobile && styleSettings.shopCardImageMobileAspectRatio)
        ? styleSettings.shopCardImageMobileAspectRatio
        : styleSettings.shopCardImageAspectRatio;
    const hasAspectRatio = activeImageAspectRatio && activeImageAspectRatio !== 'auto';
    const cardImageHeight = (isMobile && styleSettings.shopCardImageMobileHeight)
        ? (styleSettings.shopCardImageMobileHeight === 'auto' ? 'auto' : addUnit(styleSettings.shopCardImageMobileHeight))
        : (isHorizontal
            ? ((cardHeight && cardHeight !== 'auto') ? '100%' : (hasAspectRatio ? 'auto' : (styleSettings.shopCardHorizontalImageHeight || styleSettings.shopCardImageHeight || 'auto')))
            : (hasAspectRatio ? 'auto' : (styleSettings.shopCardImageHeight || '220px')));
    const cardImageAspectRatio = hasAspectRatio ? activeImageAspectRatio : undefined;
    const cardImageFit = (isMobile && styleSettings.shopCardImageMobileFit)
        ? styleSettings.shopCardImageMobileFit
        : (styleSettings.shopImageObjectFit || styleSettings.shopCardImageFit || 'cover');

    const cardImgFilter = resolveImageFilter({
        imageDesaturate: styleSettings.shopCardImageDesaturate !== undefined ? styleSettings.shopCardImageDesaturate : styleSettings.imageDesaturate,
        imageFilter: styleSettings.shopCardImageFilter || styleSettings.imageFilter,
        imageCustomFilter: styleSettings.shopCardImageCustomFilter || styleSettings.imageCustomFilter,
    });
    const cardImgTint = styleSettings.shopCardImageTint || styleSettings.imageTint || '';
    const cardImgTintOpacity = styleSettings.shopCardImageTintOpacity !== undefined ? parseFloat(styleSettings.shopCardImageTintOpacity) : (styleSettings.imageTintOpacity !== undefined ? parseFloat(styleSettings.imageTintOpacity) : 0.4);
    const cardImgTintBlendMode = styleSettings.shopCardImageTintBlendMode || styleSettings.imageTintBlendMode || 'multiply';

    const [isImageToggled, setIsImageToggled] = React.useState(false);
    const [isCardHovered, setIsCardHovered] = React.useState(false);
    const cardTransDur = styleSettings.shopCardImageTransitionDuration || styleSettings.imageTransitionDuration || '0.4s';
    const cardTransTiming = styleSettings.shopCardImageTransitionTiming || styleSettings.imageTransitionTiming || 'cubic-bezier(0.4, 0, 0.2, 1)';
    const cardClickMode = styleSettings.shopCardImageClickInteraction || styleSettings.imageClickInteraction || 'none';
    const cardHoverFilter = styleSettings.shopCardImageHoverFilter || styleSettings.imageHoverFilter || 'none';

    const isColorRevealed = isImageToggled || (isCardHovered && (cardHoverFilter === 'color-on-hover' || cardHoverFilter === 'zoom-color-on-hover' || cardHoverFilter === 'zoom-color'));
    const isDesatHover = !isImageToggled && isCardHovered && cardHoverFilter === 'desaturate-on-hover';
    const isTintBoosted = !isImageToggled && isCardHovered && cardHoverFilter === 'boost-tint-on-hover';
    const isZoomHover = isCardHovered && (cardHoverFilter === 'zoom-color-on-hover' || cardHoverFilter === 'zoom-color');

    let activeImgFilter = cardImgFilter;
    if (isColorRevealed) {
        activeImgFilter = 'none';
    } else if (isDesatHover) {
        activeImgFilter = 'grayscale(100%)';
    } else if (isCardHovered && cardHoverFilter && cardHoverFilter !== 'none') {
        activeImgFilter = cardHoverFilter;
    }

    let activeTintOpacity = cardImgTintOpacity;
    if (isColorRevealed) {
        activeTintOpacity = 0;
    } else if (isTintBoosted) {
        activeTintOpacity = Math.min(1, cardImgTintOpacity * 1.5 + 0.2);
    }

    const actionsDir = (isMobile && styleSettings.shopCardMobileActionRowDirection)
        ? styleSettings.shopCardMobileActionRowDirection
        : (styleSettings.shopCardActionsDirection || 'row');
    const btnLayout = (isMobile && styleSettings.shopCardMobileButtonsLayout)
        ? styleSettings.shopCardMobileButtonsLayout
        : (styleSettings.shopCardButtonsLayout || styleSettings.shopCardButtonsDirection || 'row');
    const isColumnBtn = btnLayout === 'column' || btnLayout === 'column-reverse' || btnLayout === 'stack' || btnLayout === 'stacked';
    const isColumnActions = actionsDir.startsWith('column') || isColumnBtn;
    const actionsAlign = styleSettings.shopCardActionsAlign || (actionsDir === 'column-center' ? 'center' : (isColumnActions ? (styleSettings.shopCardContentAlign === 'center' ? 'center' : (styleSettings.shopCardContentAlign === 'right' ? 'flex-end' : 'flex-start')) : 'space-between'));
    
    const cardBtnHeight = addUnit(
        (isMobile && styleSettings.shopCardBtnMobileHeight)
            ? styleSettings.shopCardBtnMobileHeight
            : (styleSettings.shopCardBtnHeight || '38px')
    );
    const cardBtnPadding = (isMobile && styleSettings.shopCardBtnMobilePadding)
        ? addUnit(styleSettings.shopCardBtnMobilePadding)
        : (styleSettings.btnPadding || styleSettings.shopCardBtnPadding || '0 16px');
    const cardBtnFontSize = addUnit(
        (isMobile && styleSettings.shopCardBtnMobileFontSize)
            ? styleSettings.shopCardBtnMobileFontSize
            : (styleSettings.shopCardBtnFontSize || '0.85rem')
    );
    const cardRadius = addUnit(styleSettings.btnRadius || '6px');
    const cardBtnShadow = styleSettings.btnShadow && styleSettings.btnShadow !== 'none' ? styleSettings.btnShadow : undefined;

    // Inquiry & Call for price customizability
    const showInquiryBadge = styleSettings.shopInquiryBadgeShow !== false && styleSettings.showCardInquiryBadge !== false && settings?.shopInquiryBadgeShow !== false;
    const inquiryBadgeText = styleSettings.shopInquiryBadgeText || styleSettings.inquiryBadgeText || settings?.shopInquiryBadgeText || 'Inquiry Only';
    const inquiryBadgeBg = styleSettings.shopInquiryBadgeBg || settings?.shopInquiryBadgeBg || '#f59e0b';
    const inquiryBadgeColor = styleSettings.shopInquiryBadgeColor || settings?.shopInquiryBadgeColor || '#ffffff';
    const inquiryBadgeRadius = addUnit(styleSettings.shopInquiryBadgeRadius !== undefined ? styleSettings.shopInquiryBadgeRadius : (settings?.shopInquiryBadgeRadius !== undefined ? settings.shopInquiryBadgeRadius : '4px'));
    const inquiryBadgeFontSize = addUnit(styleSettings.shopInquiryBadgeFontSize || settings?.shopInquiryBadgeFontSize || '0.75rem');
    const inquiryBadgeBorder = styleSettings.shopInquiryBadgeBorder || settings?.shopInquiryBadgeBorder || 'none';

    const inquiryBtnText = styleSettings.shopInquiryBtnText || settings?.shopInquiryBtnText || 'Inquiry Only';
    const inquiryBtnBg = styleSettings.shopInquiryBtnBg || settings?.shopInquiryBtnBg || '#cbd5e1';
    const inquiryBtnColor = styleSettings.shopInquiryBtnColor || settings?.shopInquiryBtnColor || '#64748b';

    const isProductPublished = !(product.hidden || product.status === 'draft' || product.status === 'hidden');
    const callForPriceText = styleSettings.shopCallForPriceText || styleSettings.callForPriceText || settings?.shopCallForPriceText || 'Call for Price';

    const btnSplit = styleSettings.shopCardBtnSplit || (btnLayout === 'row-equal' ? '50-50' : 'auto');

    const getDetailsFlex = () => {
        if (isColumnBtn) return undefined;
        if (styleSettings.shopCardDetailsBtnWidth) return undefined;
        if (btnSplit === '30-70') return '0 0 30%';
        if (btnSplit === '35-65') return '0 0 35%';
        if (btnSplit === '40-60') return '0 0 40%';
        if (btnSplit === '25-75') return '0 0 25%';
        if (btnSplit === '50-50' || btnLayout === 'row-equal' || styleSettings.shopCardButtonsWidth === 'full') return '1 1 0px';
        return undefined;
    };

    const getCartFlex = () => {
        if (isColumnBtn) return undefined;
        if (styleSettings.shopCardCartBtnWidth) return undefined;
        if (btnSplit === '30-70') return '1 1 70%';
        if (btnSplit === '35-65') return '1 1 65%';
        if (btnSplit === '40-60') return '1 1 60%';
        if (btnSplit === '25-75') return '1 1 75%';
        if (btnSplit === '50-50' || btnLayout === 'row-equal' || styleSettings.shopCardButtonsWidth === 'full') return '1 1 0px';
        return undefined;
    };

    const btnSharedStyle = {
        height: cardBtnHeight,
        padding: cardBtnPadding,
        borderRadius: cardRadius,
        fontFamily: (styleSettings.shopCardBtnFontFamily && styleSettings.shopCardBtnFontFamily !== 'inherit') ? styleSettings.shopCardBtnFontFamily : undefined,
        fontSize: cardBtnFontSize,
        fontWeight: styleSettings.shopCardBtnFontWeight || '600',
        letterSpacing: styleSettings.shopCardBtnLetterSpacing || 'normal',
        textTransform: styleSettings.shopCardBtnTransform || 'none',
        boxSizing: 'border-box',
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        whiteSpace: 'nowrap'
    };

    const renderDetailsBtn = () => {
        const showBtnText = !(styleSettings.shopCardHideBtnText === true || styleSettings.shopCardHideBtnText === 'true');
        if (isEditing) {
            return (
                <button
                    type="button"
                    className="shop-card-btn shop-card-details-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEditProductDetails && onEditProductDetails(product);
                    }}
                    title="Click to edit full product details & variations"
                    style={{
                        ...btnSharedStyle,
                        flex: getDetailsFlex(),
                        width: isColumnBtn ? '100%' : (styleSettings.shopCardDetailsBtnWidth ? addUnit(styleSettings.shopCardDetailsBtnWidth) : ((btnLayout === 'details-only' && styleSettings.shopCardButtonsWidth === 'full') ? '100%' : undefined)),
                        minWidth: styleSettings.shopCardBtnMinWidth ? addUnit(styleSettings.shopCardBtnMinWidth) : undefined,
                        background: styleSettings.detailBtnBg || '#f8fafc',
                        color: styleSettings.detailBtnColor || '#475569',
                        border: styleSettings.detailBtnBorder || '1px solid #cbd5e1',
                        boxShadow: styleSettings.detailBtnShadow || 'none',
                        cursor: 'pointer'
                    }}
                    onMouseOver={e => {
                        if (styleSettings.detailBtnHoverBg) e.currentTarget.style.background = styleSettings.detailBtnHoverBg;
                        if (styleSettings.detailBtnHoverColor) e.currentTarget.style.color = styleSettings.detailBtnHoverColor;
                        if (styleSettings.detailBtnHoverBorder) e.currentTarget.style.borderColor = styleSettings.detailBtnHoverBorder;
                        if (!styleSettings.detailBtnHoverBg && !styleSettings.detailBtnHoverColor) e.currentTarget.style.opacity = '0.85';
                    }}
                    onMouseOut={e => {
                        e.currentTarget.style.background = styleSettings.detailBtnBg || '#f8fafc';
                        e.currentTarget.style.color = styleSettings.detailBtnColor || '#475569';
                        e.currentTarget.style.border = styleSettings.detailBtnBorder || '1px solid #cbd5e1';
                        e.currentTarget.style.opacity = '1';
                    }}
                >
                    {showBtnText ? "Details" : "📄"}
                </button>
            );
        }
        return (
            <Link 
                to={normalizeAppUrl(`/product/${product.slug || product.id}`)} 
                className="shop-card-btn shop-card-details-btn"
                style={{
                    ...btnSharedStyle,
                    flex: getDetailsFlex(),
                    width: isColumnBtn ? '100%' : (styleSettings.shopCardDetailsBtnWidth ? addUnit(styleSettings.shopCardDetailsBtnWidth) : ((btnLayout === 'details-only' && styleSettings.shopCardButtonsWidth === 'full') ? '100%' : undefined)),
                    minWidth: styleSettings.shopCardBtnMinWidth ? addUnit(styleSettings.shopCardBtnMinWidth) : undefined,
                    background: styleSettings.detailBtnBg || '#f8fafc',
                    color: styleSettings.detailBtnColor || '#475569',
                    border: styleSettings.detailBtnBorder || '1px solid #cbd5e1',
                    boxShadow: styleSettings.detailBtnShadow || 'none'
                }}
                onMouseOver={e => {
                    if (styleSettings.detailBtnHoverBg) e.currentTarget.style.background = styleSettings.detailBtnHoverBg;
                    if (styleSettings.detailBtnHoverColor) e.currentTarget.style.color = styleSettings.detailBtnHoverColor;
                    if (styleSettings.detailBtnHoverBorder) e.currentTarget.style.borderColor = styleSettings.detailBtnHoverBorder;
                    if (!styleSettings.detailBtnHoverBg && !styleSettings.detailBtnHoverColor) e.currentTarget.style.opacity = '0.85';
                }}
                onMouseOut={e => {
                    e.currentTarget.style.background = styleSettings.detailBtnBg || '#f8fafc';
                    e.currentTarget.style.color = styleSettings.detailBtnColor || '#475569';
                    e.currentTarget.style.border = styleSettings.detailBtnBorder || '1px solid #cbd5e1';
                    e.currentTarget.style.opacity = '1';
                }}
            >
                {showBtnText ? "Details" : "📄"}
            </Link>
        );
    };

    const renderCardQtySelector = () => {
        if (styleSettings.shopCardShowQuantity === false || styleSettings.shopCardShowQuantity === 'false') return null;
        // In commercial mode, neither inquiryOnly nor stock=0 hides the qty selector
        if (!isCommercialMode && (product.inquiryOnly || product.stock === 0)) return null;
        const cardQty = tableQuantities[product.id] || 1;
        const isFullWidth = (btnLayout === 'row-equal' || styleSettings.shopCardButtonsWidth === 'full');
        return (
            <div 
                className="shop-card-qty-wrap"
                onClick={(e) => e.stopPropagation()}
                style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    border: styleSettings.shopQuantityBorder || styleSettings.detailBtnBorder || '1px solid #cbd5e1', 
                    borderRadius: styleSettings.shopQuantityRadius || cardRadius, 
                    overflow: 'hidden', 
                    height: cardBtnHeight, 
                    background: styleSettings.shopQuantityBg || '#ffffff', 
                    boxSizing: 'border-box',
                    width: isColumnBtn ? '100%' : undefined,
                    flex: isColumnBtn ? '0 0 auto' : (isFullWidth ? '1 1 0px' : undefined),
                    minWidth: isColumnBtn ? '100%' : (isFullWidth ? undefined : '95px')
                }}
            >
                <button
                    type="button"
                    disabled={isEditing}
                    onClick={(e) => { e.stopPropagation(); if (handleTableQtyChange) handleTableQtyChange(product.id, cardQty - 1); }}
                    style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        border: 'none', 
                        background: styleSettings.shopQuantityBtnBg || '#f1f5f9', 
                        padding: '0 10px', 
                        height: '100%', 
                        cursor: isEditing ? 'default' : 'pointer', 
                        fontWeight: 'bold', 
                        color: styleSettings.shopQuantityBtnColor || '#475569', 
                        fontSize: cardBtnFontSize, 
                        lineHeight: 1,
                        userSelect: 'none'
                    }}
                >
                    -
                </button>
                <input
                    type="number"
                    min="1"
                    value={cardQty}
                    disabled={isEditing}
                    onChange={(e) => { if (handleTableQtyChange) handleTableQtyChange(product.id, e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                        width: '36px', 
                        height: '100%', 
                        margin: 0, 
                        padding: '0 2px', 
                        textAlign: 'center', 
                        border: 'none', 
                        fontWeight: '700', 
                        fontSize: cardBtnFontSize, 
                        color: styleSettings.shopQuantityTextColor || '#0f172a', 
                        outline: 'none', 
                        background: 'transparent', 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        lineHeight: 1, 
                        boxSizing: 'border-box' 
                    }}
                />
                <button
                    type="button"
                    disabled={isEditing}
                    onClick={(e) => { e.stopPropagation(); if (handleTableQtyChange) handleTableQtyChange(product.id, cardQty + 1); }}
                    style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        border: 'none', 
                        background: styleSettings.shopQuantityBtnBg || '#f1f5f9', 
                        padding: '0 10px', 
                        height: '100%', 
                        cursor: isEditing ? 'default' : 'pointer', 
                        fontWeight: 'bold', 
                        color: styleSettings.shopQuantityBtnColor || '#475569', 
                        fontSize: cardBtnFontSize, 
                        lineHeight: 1,
                        userSelect: 'none'
                    }}
                >
                    +
                </button>
            </div>
        );
    };

    const renderCartBtn = () => {
        const cardQty = tableQuantities[product.id] || 1;
        // In commercial mode, neither stock=0 nor inquiryOnly disables the button
        const isZeroStock = cardStock === 0;
        const isSoldOutOrInquiry = !isCommercialMode && !isEditing && (isZeroStock || product.inquiryOnly);
        const btnBackground = (!isCommercialMode && product.inquiryOnly)
            ? inquiryBtnBg 
            : (isSoldOutOrInquiry ? '#cbd5e1' : (styleSettings.btnColor || primaryColor));
        const btnTextColor = (!isCommercialMode && product.inquiryOnly)
            ? inquiryBtnColor 
            : (isSoldOutOrInquiry ? '#64748b' : (styleSettings.btnTextColor || '#ffffff'));

        const showBtnText = !(styleSettings.shopCardHideBtnText === true || styleSettings.shopCardHideBtnText === 'true');
        const fullLabel = (!isCommercialMode && isZeroStock) ? "Sold Out" : (!isCommercialMode && product.inquiryOnly) ? inquiryBtnText : "Add to Cart";
        const iconLabel = (!isCommercialMode && isZeroStock) ? "❌" : (!isCommercialMode && product.inquiryOnly) ? "✉️" : "🛒";

        return (
            <button 
                type="button"
                className="shop-card-btn shop-card-cart-btn"
                onClick={(e) => {
                    if (isEditing) {
                        e.stopPropagation();
                        e.preventDefault();
                        return;
                    }
                    if (!isSoldOutOrInquiry && addToCart) {
                        addToCart(product, cardQty, hasSelectableOptions ? effectiveOptions : null, matchedVar);
                    }
                }}
                disabled={!isEditing && isSoldOutOrInquiry}
                style={{ 
                    ...btnSharedStyle,
                    flex: getCartFlex(),
                    width: isColumnBtn ? '100%' : (styleSettings.shopCardCartBtnWidth ? addUnit(styleSettings.shopCardCartBtnWidth) : ((btnLayout === 'cart-only' && styleSettings.shopCardButtonsWidth === 'full') ? '100%' : undefined)),
                    minWidth: styleSettings.shopCardBtnMinWidth ? addUnit(styleSettings.shopCardBtnMinWidth) : undefined,
                    background: btnBackground, 
                    color: btnTextColor, 
                    border: isSoldOutOrInquiry ? (product.inquiryOnly && styleSettings.shopInquiryBtnBorder ? styleSettings.shopInquiryBtnBorder : 'none') : (styleSettings.btnBorder || 'none'), 
                    boxShadow: isSoldOutOrInquiry ? 'none' : cardBtnShadow,
                    cursor: isEditing ? 'pointer' : (isSoldOutOrInquiry ? 'not-allowed' : 'pointer')
                }}
                onMouseOver={e => {
                    if (!isSoldOutOrInquiry) {
                        if (styleSettings.btnHoverBg) e.currentTarget.style.background = styleSettings.btnHoverBg;
                        if (styleSettings.btnHoverColor) e.currentTarget.style.color = styleSettings.btnHoverColor;
                        if (styleSettings.btnHoverBorder) e.currentTarget.style.borderColor = styleSettings.btnHoverBorder;
                        if (styleSettings.btnHoverShadow) e.currentTarget.style.boxShadow = styleSettings.btnHoverShadow;
                        if (!styleSettings.btnHoverBg && !styleSettings.btnHoverColor) e.currentTarget.style.filter = 'brightness(1.08)';
                    }
                }}
                onMouseOut={e => {
                    if (!isSoldOutOrInquiry) {
                        e.currentTarget.style.background = styleSettings.btnColor || primaryColor;
                        e.currentTarget.style.color = styleSettings.btnTextColor || '#ffffff';
                        e.currentTarget.style.border = styleSettings.btnBorder || 'none';
                        e.currentTarget.style.boxShadow = cardBtnShadow || 'none';
                        e.currentTarget.style.filter = 'none';
                    }
                }}
            >
                {showBtnText ? fullLabel : iconLabel}
            </button>
        );
    };

    const rawPad = (isMobile && styleSettings.shopCardMobilePadding)
        ? styleSettings.shopCardMobilePadding
        : (isHorizontal
            ? (styleSettings.shopCardHorizontalContentPadding || styleSettings.shopCardContentPadding || styleSettings.shopCardPadding || '20px')
            : (styleSettings.shopCardContentPadding || styleSettings.shopCardPadding || '20px'));

    const padTop = isHorizontal 
        ? (styleSettings.shopCardHorizontalContentPaddingTop ?? styleSettings.shopCardContentPaddingTop) 
        : styleSettings.shopCardContentPaddingTop;
    const padSides = isHorizontal
        ? (styleSettings.shopCardHorizontalContentPaddingSides ?? styleSettings.shopCardContentPaddingSides)
        : styleSettings.shopCardContentPaddingSides;
    const padRight = (isHorizontal 
        ? (styleSettings.shopCardHorizontalContentPaddingRight ?? styleSettings.shopCardContentPaddingRight) 
        : styleSettings.shopCardContentPaddingRight) ?? padSides;
    const padBottom = isHorizontal 
        ? (styleSettings.shopCardHorizontalContentPaddingBottom ?? styleSettings.shopCardContentPaddingBottom) 
        : styleSettings.shopCardContentPaddingBottom;
    const padLeft = (isHorizontal 
        ? (styleSettings.shopCardHorizontalContentPaddingLeft ?? styleSettings.shopCardContentPaddingLeft) 
        : styleSettings.shopCardContentPaddingLeft) ?? padSides;

    const hasIndividualPadding = !(isMobile && styleSettings.shopCardMobilePadding) && (
                                 (padTop !== undefined && padTop !== '') || 
                                 (padRight !== undefined && padRight !== '') || 
                                 (padBottom !== undefined && padBottom !== '') || 
                                 (padLeft !== undefined && padLeft !== ''));

    const contentPaddingStyle = hasIndividualPadding
        ? {
            paddingTop: padTop !== undefined && padTop !== '' ? addUnit(padTop) : addUnit(rawPad),
            paddingRight: padRight !== undefined && padRight !== '' ? addUnit(padRight) : addUnit(rawPad),
            paddingBottom: padBottom !== undefined && padBottom !== '' ? addUnit(padBottom) : addUnit(rawPad),
            paddingLeft: padLeft !== undefined && padLeft !== '' ? addUnit(padLeft) : addUnit(rawPad),
        }
        : {
            padding: addUnit(rawPad)
        };

    const effectiveGraphicSettings = {
        ...styleSettings,
        ...(product?.graphicBorder ? { shopImageGraphicBorder: product.graphicBorder } : {}),
        ...(product?.graphicBorderUrl ? { shopImageGraphicBorderUrl: product.graphicBorderUrl } : {}),
        ...(product?.graphicBorderWidth ? { shopImageGraphicBorderWidth: product.graphicBorderWidth } : {}),
        ...(product?.graphicBorderColor ? { shopImageGraphicBorderColor: product.graphicBorderColor } : {}),
        ...(product?.graphicBorderPadding ? { shopImageGraphicBorderPadding: product.graphicBorderPadding } : {})
    };
    const graphicBorderStyle = getGraphicBorderStyle(effectiveGraphicSettings);
    const graphicBorderPadding = getGraphicBorderPadding(effectiveGraphicSettings);

    const imgPadTop = styleSettings.shopImagePaddingTop ?? styleSettings.shopCardImagePaddingTop;
    const imgPadBottom = styleSettings.shopImagePaddingBottom ?? styleSettings.shopCardImagePaddingBottom;
    const imgPadSides = styleSettings.shopImagePaddingSides ?? styleSettings.shopCardImagePaddingSides;
    const imgPadLeft = styleSettings.shopImagePaddingLeft ?? styleSettings.shopCardImagePaddingLeft ?? imgPadSides;
    const imgPadRight = styleSettings.shopImagePaddingRight ?? styleSettings.shopCardImagePaddingRight ?? imgPadSides;

    const hasGranularImgPad = !(isMobile && styleSettings.shopImageMobilePadding) && (
                              (imgPadTop !== undefined && imgPadTop !== '') ||
                              (imgPadRight !== undefined && imgPadRight !== '') ||
                              (imgPadBottom !== undefined && imgPadBottom !== '') ||
                              (imgPadLeft !== undefined && imgPadLeft !== ''));

    const rawImgPad = (isMobile && styleSettings.shopImageMobilePadding !== undefined && styleSettings.shopImageMobilePadding !== '')
        ? addUnit(styleSettings.shopImageMobilePadding)
        : (styleSettings.shopImagePadding || styleSettings.shopCardImagePadding || '0px');

    const shorthandImgSides = (typeof rawImgPad === 'string' && rawImgPad) ? (() => {
        const parts = rawImgPad.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 1) return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
        if (parts.length === 2) return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
        if (parts.length === 3) return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
        if (parts.length >= 4) return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
        return null;
    })() : null;

    const resolvedImgPadTop = (graphicBorderPadding === null && hasGranularImgPad)
        ? addUnit(imgPadTop !== undefined && imgPadTop !== '' ? imgPadTop : (shorthandImgSides?.top || '0px'))
        : undefined;
    const resolvedImgPadRight = (graphicBorderPadding === null && hasGranularImgPad)
        ? addUnit(imgPadRight !== undefined && imgPadRight !== '' ? imgPadRight : (shorthandImgSides?.right || '0px'))
        : undefined;
    const resolvedImgPadBottom = (graphicBorderPadding === null && hasGranularImgPad)
        ? addUnit(imgPadBottom !== undefined && imgPadBottom !== '' ? imgPadBottom : (shorthandImgSides?.bottom || '0px'))
        : undefined;
    const resolvedImgPadLeft = (graphicBorderPadding === null && hasGranularImgPad)
        ? addUnit(imgPadLeft !== undefined && imgPadLeft !== '' ? imgPadLeft : (shorthandImgSides?.left || '0px'))
        : undefined;

    // Granular padding for options select
    const optPadTop = styleSettings.shopCardOptionsPaddingTop;
    const optPadBottom = styleSettings.shopCardOptionsPaddingBottom;
    const optPadSides = styleSettings.shopCardOptionsPaddingSides;
    const optPadLeft = styleSettings.shopCardOptionsPaddingLeft ?? optPadSides;
    const optPadRight = styleSettings.shopCardOptionsPaddingRight ?? optPadSides;
    const hasGranularOptPad = (optPadTop !== undefined && optPadTop !== '') ||
                              (optPadRight !== undefined && optPadRight !== '') ||
                              (optPadBottom !== undefined && optPadBottom !== '') ||
                              (optPadLeft !== undefined && optPadLeft !== '');

    const contentAlign = isHorizontal
        ? (styleSettings.shopCardHorizontalContentAlign || styleSettings.shopCardContentAlign || 'left')
        : (styleSettings.shopCardContentAlign || 'left');

    const contentJustify = isHorizontal
        ? (styleSettings.shopCardHorizontalContentJustify || styleSettings.shopCardContentJustify || 'space-between')
        : (styleSettings.shopCardContentJustify || 'space-between');

    const isSpreadVertical = (contentJustify === 'space-between');

    const contentGap = addUnit(
        (isMobile && styleSettings.shopCardContentMobileGap !== undefined && styleSettings.shopCardContentMobileGap !== '')
            ? styleSettings.shopCardContentMobileGap
            : (isHorizontal && styleSettings.shopCardHorizontalContentGap !== undefined && styleSettings.shopCardHorizontalContentGap !== ''
                ? styleSettings.shopCardHorizontalContentGap
                : (styleSettings.shopCardContentGap || '10px'))
    );

    const contentBg = (isHorizontal && styleSettings.shopCardHorizontalContentBg)
        ? styleSettings.shopCardHorizontalContentBg
        : (styleSettings.shopCardContentBg || 'transparent');

    const actionRowSplit = styleSettings.shopCardActionRowSplit || 'auto';
    const priceColWidth = styleSettings.shopCardPriceColWidth ? addUnit(styleSettings.shopCardPriceColWidth) : (
        actionRowSplit === 'split-30-70' ? '30%' : (
        actionRowSplit === 'split-40-60' ? '40%' : (
        actionRowSplit === 'split-50-50' ? '50%' : (
        actionRowSplit === 'column' ? '100%' : undefined
    ))));

    const buttonsColWidth = styleSettings.shopCardButtonsColWidth ? addUnit(styleSettings.shopCardButtonsColWidth) : (
        actionRowSplit === 'split-30-70' ? '70%' : (
        actionRowSplit === 'split-40-60' ? '60%' : (
        actionRowSplit === 'split-50-50' ? '50%' : (
        (btnLayout === 'column' || btnLayout === 'column-reverse' || btnLayout === 'row-equal' || styleSettings.shopCardButtonsWidth === 'full' || isColumnActions || actionRowSplit === 'column') ? '100%' : 'auto'
    ))));

    const buttonsColFlex = (actionRowSplit === 'price-auto-buttons-fill')
        ? '1 1 0px'
        : ((actionRowSplit === 'split-30-70' || actionRowSplit === 'split-40-60' || actionRowSplit === 'split-50-50') ? '1 1 0px' : undefined);

    // Card DOM anchor ID
    const cardDomId = product.cardAnchorId || product.anchorId || product.cardId || (product.slug ? `product-${product.slug}` : `product-${product.id}`);

    // Extract all product categories (supports array, JSON string, comma string, pageConfig)
    const productCategories = useMemo(() => {
        const cats = [];
        if (Array.isArray(product.categories)) {
            product.categories.forEach(c => {
                if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
            });
        } else if (typeof product.categories === 'string' && product.categories.trim()) {
            try {
                const parsed = JSON.parse(product.categories);
                if (Array.isArray(parsed)) {
                    parsed.forEach(c => {
                        if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                        else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
                    });
                }
            } catch (e) {
                if (product.categories.includes(',')) {
                    product.categories.split(',').forEach(c => { if (c.trim()) cats.push(c.trim()); });
                } else {
                    cats.push(product.categories.trim());
                }
            }
        }
        if (product.pageConfig && Array.isArray(product.pageConfig.categories)) {
            product.pageConfig.categories.forEach(c => {
                if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
            });
        }
        if (typeof product.category === 'string' && product.category.trim()) {
            if (product.category.includes(',')) {
                product.category.split(',').forEach(c => { if (c.trim()) cats.push(c.trim()); });
            } else {
                cats.push(product.category.trim());
            }
        } else if (product.category && typeof product.category === 'object' && product.category.name) {
            cats.push(String(product.category.name).trim());
        }
        return Array.from(new Set(cats.filter(Boolean)));
    }, [product.categories, product.category, product.pageConfig]);

    // Primary category name & thumbnail lookup
    const prodCategoryName = (productCategories[0] || product.category || '').trim();
    const catObj = (dbCategories || []).find(c => 
        (c.name && prodCategoryName && c.name.toLowerCase() === prodCategoryName.toLowerCase()) ||
        (c.slug && prodCategoryName && c.slug.toLowerCase() === prodCategoryName.toLowerCase()) ||
        (prodCategoryName && slugify(c.name || '') === slugify(prodCategoryName))
    );
    const catImageUrl = product.categoryImage || product.catImage || catObj?.image;
    const resolvedCatImg = catImageUrl ? resolveMediaUrl(catImageUrl) : null;

    const cardCatDisplayMode = styleSettings.shopCardCategoryDisplay || styleSettings.shopCardCatDisplayMode || styleSettings.shopCatDisplayMode || 'text'; // 'text' | 'image' | 'both'
    const cardCatImagePosition = styleSettings.shopCardCatImagePosition || styleSettings.shopCatImagePosition || 'left'; // 'left' | 'right' | 'top' | 'bottom'
    const cardCatImageWidth = addUnit(styleSettings.shopCardCatImageWidth || styleSettings.shopCardCatImageSize || styleSettings.shopCatImageWidth || styleSettings.shopCatImageSize || '20px');
    const cardCatImageHeight = addUnit(styleSettings.shopCardCatImageHeight || styleSettings.shopCardCatImageSize || styleSettings.shopCatImageHeight || styleSettings.shopCatImageSize || cardCatImageWidth);
    const cardCatImageRadius = addUnit(styleSettings.shopCardCatImageRadius !== undefined ? styleSettings.shopCardCatImageRadius : (styleSettings.shopCatImageRadius !== undefined ? styleSettings.shopCatImageRadius : '50%'));
    const cardCatImageFit = styleSettings.shopCardCatImageFit || styleSettings.shopCatImageFit || 'cover';
    const cardCatImageBorder = styleSettings.shopCardCatImageBorder || styleSettings.shopCatImageBorder || 'none';
    const cardCatImageShadow = styleSettings.shopCardCatImageShadow || styleSettings.shopCatImageShadow || 'none';
    const cardCatImageBg = styleSettings.shopCardCatImageBg || styleSettings.shopCatImageBg || 'transparent';
    const cardCatImagePadding = addUnit(styleSettings.shopCardCatImagePadding || styleSettings.shopCatImagePadding || '0px');
    const cardCatImageGap = addUnit(styleSettings.shopCardCatImageGap || styleSettings.shopCatImageGap || '6px');

    const cardCatBg = styleSettings.shopProductCategoryBg || styleSettings.shopCardCategoryBg || styleSettings.shopCatBtnBg || 'transparent';
    const cardCatBorder = styleSettings.shopProductCategoryBorder || styleSettings.shopCardCategoryBorder || styleSettings.shopCatBtnBorder || 'none';
    const cardCatRadius = addUnit(styleSettings.shopProductCategoryRadius || styleSettings.shopCardCategoryRadius || styleSettings.shopCatBtnRadius || '0px');
    const cardCatPadding = addUnit(styleSettings.shopProductCategoryPadding || styleSettings.shopCardCategoryPadding || styleSettings.shopCatBtnPadding || '0px');

    const renderCardCategoryContent = (editingMode = false) => {
        const textContent = editingMode ? (
            <input
                type="text"
                className="shop-product-category-input"
                value={productCategories.length > 0 ? productCategories.join(', ') : (product.category || '')}
                onChange={e => {
                    const val = e.target.value;
                    const parts = val.split(',').map(s => s.trim()).filter(Boolean);
                    if (updateProductField) {
                        updateProductField(product.id, 'category', val);
                        updateProductField(product.id, 'categories', parts);
                    }
                }}
                placeholder="Category"
                title="Edit categories (comma separated for multiple)"
                style={{
                    ...resolveFontStyles('body', {
                        align: settings.shopProductCategoryAlign || contentAlign,
                        preset: settings.shopProductCategoryPreset
                    }),
                    fontFamily: (styleSettings.shopProductCategoryFontFamily && styleSettings.shopProductCategoryFontFamily !== 'inherit') ? styleSettings.shopProductCategoryFontFamily : ((settings.shopProductCategoryFontFamily && settings.shopProductCategoryFontFamily !== 'inherit') ? settings.shopProductCategoryFontFamily : undefined),
                    fontSize: addUnit(styleSettings.shopProductCategorySize !== undefined && styleSettings.shopProductCategorySize !== '' ? styleSettings.shopProductCategorySize : (settings.shopProductCategorySize || '0.8rem')),
                    color: styleSettings.shopProductCategoryColor || settings.shopProductCategoryColor || primaryColor,
                    fontWeight: styleSettings.shopProductCategoryWeight || settings.shopProductCategoryWeight || 'bold',
                    textTransform: styleSettings.shopProductCategoryTransform || settings.shopProductCategoryTransform || 'uppercase',
                    display: 'inline-block',
                    background: 'rgba(59,130,246,0.08)',
                    border: '1px dashed rgba(59,130,246,0.4)',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    outline: 'none',
                    minWidth: '60px',
                    boxSizing: 'border-box',
                    cursor: 'text'
                }}
            />
        ) : (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center' }}>
                {productCategories.length > 0 ? (
                    productCategories.map((cat, idx) => (
                        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {idx > 0 && (
                                <span style={{ opacity: 0.5, margin: '0 4px', userSelect: 'none' }}>
                                    {styleSettings.shopCategorySeparator || ','}
                                </span>
                            )}
                            <span
                                style={{
                                    cursor: setActiveCategory ? 'pointer' : 'inherit',
                                    transition: 'opacity 0.15s ease'
                                }}
                                onClick={setActiveCategory ? (e) => {
                                    e.stopPropagation();
                                    setActiveCategory(cat);
                                    if (setActiveSubCategory) setActiveSubCategory('All');
                                } : undefined}
                                title={setActiveCategory ? `Filter by ${cat}` : undefined}
                                onMouseEnter={setActiveCategory ? (e) => { e.currentTarget.style.opacity = '0.75'; } : undefined}
                                onMouseLeave={setActiveCategory ? (e) => { e.currentTarget.style.opacity = '1'; } : undefined}
                            >
                                {stripHtml(cat)}
                            </span>
                        </span>
                    ))
                ) : (
                    <span>{stripHtml(product.category) || ''}</span>
                )}
                {product.subCategory && (
                    <span style={{ opacity: 0.7, color: styleSettings.shopCategoryPathColor || undefined }}>
                        {' ' + (styleSettings.shopCategoryPathSeparator || settings.shopCategoryPathSeparator || '›') + ' '}
                        {stripHtml(product.subCategory)}
                    </span>
                )}
            </span>
        );

        const imgElement = resolvedCatImg ? (
            <img 
                src={resolvedCatImg} 
                alt="" 
                style={{
                    width: cardCatImageWidth,
                    height: cardCatImageHeight,
                    borderRadius: cardCatImageRadius,
                    objectFit: cardCatImageFit,
                    border: cardCatImageBorder,
                    boxShadow: cardCatImageShadow,
                    background: cardCatImageBg,
                    padding: cardCatImagePadding,
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    display: 'block'
                }}
            />
        ) : (editingMode && (cardCatDisplayMode === 'image' || cardCatDisplayMode === 'both') ? (
            <div 
                title="Category Image Placeholder (Set image in Category settings)"
                style={{
                    width: cardCatImageWidth,
                    height: cardCatImageHeight,
                    borderRadius: cardCatImageRadius,
                    border: cardCatImageBorder !== 'none' ? cardCatImageBorder : '1px dashed #93c5fd',
                    background: cardCatImageBg !== 'transparent' ? cardCatImageBg : '#eff6ff',
                    color: '#3b82f6',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    flexShrink: 0
                }}
            >
                📷
            </div>
        ) : null);

        if (cardCatDisplayMode === 'image' && imgElement) {
            return (
                <span title={productCategories.join(', ') || stripHtml(product.category) || ''} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {imgElement}
                </span>
            );
        }

        if (cardCatDisplayMode === 'both' && imgElement) {
            const flexDir = cardCatImagePosition === 'top' ? 'column' : (cardCatImagePosition === 'bottom' ? 'column-reverse' : (cardCatImagePosition === 'right' ? 'row-reverse' : 'row'));
            const isCol = cardCatImagePosition === 'top' || cardCatImagePosition === 'bottom';
            return (
                <span style={{
                    display: 'inline-flex',
                    alignItems: isCol ? (contentAlign === 'center' ? 'center' : (contentAlign === 'right' ? 'flex-end' : 'flex-start')) : 'center',
                    justifyContent: contentAlign === 'center' ? 'center' : (contentAlign === 'right' ? 'flex-end' : 'flex-start'),
                    flexDirection: flexDir,
                    gap: cardCatImageGap,
                    flexWrap: 'wrap'
                }}>
                    {imgElement}
                    {textContent}
                </span>
            );
        }

        return textContent;
    };

    const showDescription = styleSettings.shopProductDescShow !== false && styleSettings.shopProductDescShow !== 'false';

    return (
        <div 
            id={cardDomId}
            data-product-id={product.id}
            data-product-slug={product.slug || ''}
            className="shop-product-card"
            onMouseEnter={() => setIsCardHovered(true)}
            onMouseLeave={() => setIsCardHovered(false)}
            style={{
                background: styleSettings.shopCardBg || '#ffffff',
                borderRadius: styleSettings.shopCardBorderRadius || '12px',
                overflow: 'hidden',
                border: styleSettings.shopCardBorder || '1px solid #e2e8f0',
                boxShadow: styleSettings.productCardShadow || '0 4px 6px -1px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: cardDirection,
                height: (cardHeight && cardHeight !== 'auto') ? addUnit(cardHeight) : undefined,
                minHeight: cardMinHeight ? addUnit(cardMinHeight) : undefined,
                maxHeight: cardMaxHeight ? addUnit(cardMaxHeight) : undefined,
                transition: 'transform 0.2s, box-shadow 0.2s, outline 0.15s ease',
                cursor: (isEditing && isSimpleMode) ? 'pointer' : (isEditing ? 'default' : 'pointer'),
                outline: (isEditing && isSimpleMode && selectedSimpleSection === 'cards') ? '2px dashed #2563eb' : undefined,
                outlineOffset: (isEditing && isSimpleMode && selectedSimpleSection === 'cards') ? '2px' : undefined
            }}
            title={(isEditing && isSimpleMode) ? "Click to edit Product Card options" : undefined}
            onClick={e => {
                if (isEditing && isSimpleMode) {
                    e.stopPropagation();
                    e.preventDefault();
                    if (onSectionSelect) onSectionSelect('cards');
                    return;
                }
                if (isEditing) {
                    return;
                }
                if (!e.target.closest('a, button, input, textarea, select')) {
                    navigate(normalizeAppUrl(`/product/${product.slug || product.id}`));
                }
            }}
            onMouseOver={e => {
                if (isEditing) return;
                if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = styleSettings.productCardShadow === 'none' ? 'none' : '0 10px 15px -3px rgba(0,0,0,0.1)';
            }}
            onMouseOut={e => {
                if (isEditing) return;
                if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = styleSettings.productCardShadow || '0 4px 6px -1px rgba(0,0,0,0.05)';
            }}
        >
            <div className="shop-product-card-img-wrap" style={{ 
                position: 'relative',
                width: (isMobile && styleSettings.shopCardImageMobileWidth)
                    ? addUnit(styleSettings.shopCardImageMobileWidth)
                    : (isHorizontal ? (styleSettings.shopCardImageWidth || '40%') : '100%'),
                height: (isHorizontal && cardHeight && cardHeight !== 'auto') ? '100%' : undefined,
                alignSelf: isHorizontal ? 'stretch' : undefined,
                flexShrink: 0,
                flexGrow: 0,
                overflow: 'hidden',
                background: styleSettings.shopImageBg || styleSettings.shopCardImageBg || 'transparent',
                padding: graphicBorderPadding !== null
                    ? graphicBorderPadding
                    : (hasGranularImgPad ? undefined : addUnit(rawImgPad)),
                paddingTop: resolvedImgPadTop,
                paddingRight: resolvedImgPadRight,
                paddingBottom: resolvedImgPadBottom,
                paddingLeft: resolvedImgPadLeft,
                boxSizing: 'border-box',
                border: graphicBorderStyle ? undefined : (styleSettings.shopImageBorder || 'none'),
                borderRadius: graphicBorderStyle
                    ? '0px'
                    : ((isMobile && styleSettings.shopImageMobileBorderRadius !== undefined && styleSettings.shopImageMobileBorderRadius !== '')
                        ? addUnit(styleSettings.shopImageMobileBorderRadius)
                        : (styleSettings.shopImageBorderRadius || '0px')),
                ...(graphicBorderStyle || {})
            }}>
                {isEditing && (
                    <div style={{
                        background: 'rgba(15, 23, 42, 0.95)',
                        padding: '6px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '6px',
                        borderBottom: '1px solid #334155',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{ flex: 1, minWidth: '110px' }}>
                            <MediaInput
                                size="small"
                                value={(product.images && product.images[0]) || ''}
                                onChange={e => updateProductField && updateProductField(product.id, 'images', [e.target.value])}
                                placeholder="Image URL..."
                            />
                        </div>
                        {/* Live Card Anchor / DOM ID Editor */}
                        <div 
                            title="Card Anchor ID (allows categories or links to scroll directly to this card with #id)"
                            style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '2px', 
                                background: '#1e293b', 
                                padding: '2px 6px', 
                                borderRadius: '4px', 
                                border: '1px solid #475569' 
                            }}
                        >
                            <span style={{ fontSize: '0.72rem', color: '#38bdf8', fontFamily: 'monospace', fontWeight: 'bold' }}>#</span>
                            <input
                                type="text"
                                value={product.cardAnchorId !== undefined ? product.cardAnchorId : (product.anchorId || '')}
                                onChange={e => {
                                    const clean = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
                                    updateProductField && updateProductField(product.id, 'cardAnchorId', clean);
                                }}
                                placeholder="card-id"
                                style={{
                                    width: '68px',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#7dd3fc',
                                    fontSize: '0.75rem',
                                    fontFamily: 'monospace',
                                    fontWeight: '700',
                                    outline: 'none',
                                    padding: '1px 2px'
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <button
                                type="button"
                                title={isProductPublished ? "Product is Published. Click to Unpublish (Set to Draft)" : "Product is Unpublished (Draft). Click to Publish Live"}
                                onClick={() => {
                                    const nextStatus = isProductPublished ? 'draft' : 'published';
                                    const nextHidden = isProductPublished ? true : false;
                                    if (updateProductField) {
                                        updateProductField(product.id, 'status', nextStatus);
                                        updateProductField(product.id, 'hidden', nextHidden);
                                    }
                                }}
                                style={{
                                    background: isProductPublished ? '#10b981' : '#f59e0b',
                                    color: isProductPublished ? '#ffffff' : '#000000',
                                    border: 'none',
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.72rem',
                                    fontWeight: 'bold',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                <span>{isProductPublished ? '● Published' : '○ Draft'}</span>
                            </button>
                            <button
                                type="button"
                                title="Duplicate Product"
                                onClick={() => handleDuplicateProductLive && handleDuplicateProductLive(product)}
                                style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '3px 7px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 'bold' }}
                            >⧉</button>
                            <button
                                type="button"
                                title="Delete Product"
                                onClick={() => handleDeleteProductLive && handleDeleteProductLive(product.id)}
                                style={{ background: '#ef4444', color: 'white', border: 'none', padding: '3px 7px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 'bold' }}
                            >🗑️</button>
                        </div>
                    </div>
                )}
                {isEditing ? (
                    <div 
                        className="shop-product-card-img-box"
                        style={{
                            display: 'block',
                            width: '100%',
                            height: (isHorizontal && cardHeight && cardHeight !== 'auto') ? '100%' : cardImageHeight,
                            aspectRatio: cardImageAspectRatio,
                            minHeight: (isHorizontal && (!cardHeight || cardHeight === 'auto')) ? (hasAspectRatio ? undefined : (cardMinHeight ? addUnit(cardMinHeight) : '180px')) : undefined,
                            maxHeight: isHorizontal ? (cardMaxHeight ? addUnit(cardMaxHeight) : undefined) : undefined,
                            overflow: 'hidden',
                            position: 'relative',
                            cursor: 'default'
                        }}
                    >
                        <img 
                            className="shop-product-card-img"
                            src={imageSrc} 
                            alt={product.name} 
                            onClick={cardClickMode === 'toggle' ? (e) => { e.preventDefault(); e.stopPropagation(); setIsImageToggled(prev => !prev); } : undefined}
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: cardImageFit,
                                background: styleSettings.shopProductCardImgBg || (styleSettings.shopImageBg && styleSettings.shopImageBg !== 'transparent' ? styleSettings.shopImageBg : undefined),
                                border: (styleSettings.shopProductCardImgBorder && styleSettings.shopProductCardImgBorder !== 'none') ? styleSettings.shopProductCardImgBorder : undefined,
                                borderRadius: styleSettings.shopProductCardImgRadius ? addUnit(styleSettings.shopProductCardImgRadius) : (styleSettings.shopImageInnerRadius || '0px'),
                                padding: styleSettings.shopProductCardImgPadding ? addUnit(styleSettings.shopProductCardImgPadding) : undefined,
                                boxSizing: 'border-box',
                                display: 'block',
                                filter: activeImgFilter || 'none',
                                WebkitFilter: activeImgFilter || 'none',
                                transform: isZoomHover ? 'scale(1.05)' : undefined,
                                transition: `filter ${cardTransDur} ${cardTransTiming}, -webkit-filter ${cardTransDur} ${cardTransTiming}, transform ${cardTransDur} ${cardTransTiming}`,
                                cursor: cardClickMode === 'toggle' ? 'pointer' : undefined
                            }} 
                        />
                        {Boolean(cardImgTint && cardImgTint !== 'transparent') && (
                            <div
                                className="shop-card-image-tint-overlay"
                                onClick={cardClickMode === 'toggle' ? (e) => { e.preventDefault(); e.stopPropagation(); setIsImageToggled(prev => !prev); } : undefined}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundColor: cardImgTint,
                                    opacity: activeTintOpacity,
                                    mixBlendMode: cardImgTintBlendMode,
                                    pointerEvents: cardClickMode === 'toggle' ? 'auto' : 'none',
                                    cursor: cardClickMode === 'toggle' ? 'pointer' : undefined,
                                    borderRadius: styleSettings.shopImageInnerRadius || '0px',
                                    transition: `opacity ${cardTransDur} ${cardTransTiming}, background-color ${cardTransDur} ${cardTransTiming}`,
                                    zIndex: 1
                                }}
                            />
                        )}
                        {!isProductPublished && (
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                left: '10px',
                                background: '#f59e0b',
                                color: '#000000',
                                fontSize: '0.65rem',
                                fontWeight: '800',
                                padding: '3px 7px',
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                                zIndex: 4
                            }}>
                                Draft / Unpublished
                            </div>
                        )}
                        {!isCommercialMode && product.inquiryOnly && showInquiryBadge && (
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: inquiryBadgeBg,
                                color: inquiryBadgeColor,
                                fontSize: inquiryBadgeFontSize,
                                fontWeight: 'bold',
                                padding: '4px 8px',
                                borderRadius: inquiryBadgeRadius,
                                border: inquiryBadgeBorder,
                                zIndex: 2,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                {inquiryBadgeText}
                            </div>
                        )}
                        {!isCommercialMode && cardStock === 0 && (
                            <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#ef4444', color: 'white', fontSize: '0.75rem', fontWeight: 'bold', padding: '4px 8px', borderRadius: '4px', zIndex: 2 }}>
                                Out of Stock
                            </div>
                        )}
                    </div>
                ) : (
                    <Link to={normalizeAppUrl(`/product/${product.slug || product.id}`)} className="shop-product-card-img-box" style={{ 
                        display: 'block', 
                        width: '100%',
                        height: (isHorizontal && cardHeight && cardHeight !== 'auto') ? '100%' : cardImageHeight,
                        aspectRatio: cardImageAspectRatio,
                        minHeight: (isHorizontal && (!cardHeight || cardHeight === 'auto')) ? (hasAspectRatio ? undefined : (cardMinHeight ? addUnit(cardMinHeight) : '180px')) : undefined,
                        maxHeight: isHorizontal ? (cardMaxHeight ? addUnit(cardMaxHeight) : undefined) : undefined,
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <img 
                            className="shop-product-card-img"
                            src={imageSrc} 
                            alt={stripHtml(product.name)} 
                            loading={idx < 4 ? "eager" : "lazy"}
                            decoding="async"
                            {...(idx < 4 ? { fetchpriority: "high" } : {})}
                            onClick={cardClickMode === 'toggle' ? (e) => { e.preventDefault(); e.stopPropagation(); setIsImageToggled(prev => !prev); } : undefined}
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: cardImageFit,
                                background: styleSettings.shopProductCardImgBg || (styleSettings.shopImageBg && styleSettings.shopImageBg !== 'transparent' ? styleSettings.shopImageBg : undefined),
                                border: (styleSettings.shopProductCardImgBorder && styleSettings.shopProductCardImgBorder !== 'none') ? styleSettings.shopProductCardImgBorder : undefined,
                                borderRadius: styleSettings.shopProductCardImgRadius ? addUnit(styleSettings.shopProductCardImgRadius) : (styleSettings.shopImageInnerRadius || '0px'),
                                padding: styleSettings.shopProductCardImgPadding ? addUnit(styleSettings.shopProductCardImgPadding) : undefined,
                                boxSizing: 'border-box',
                                display: 'block',
                                filter: activeImgFilter || 'none',
                                WebkitFilter: activeImgFilter || 'none',
                                transform: isZoomHover ? 'scale(1.05)' : undefined,
                                transition: `filter ${cardTransDur} ${cardTransTiming}, -webkit-filter ${cardTransDur} ${cardTransTiming}, transform ${cardTransDur} ${cardTransTiming}`,
                                cursor: cardClickMode === 'toggle' ? 'pointer' : undefined
                            }} 
                        />
                        {Boolean(cardImgTint && cardImgTint !== 'transparent') && (
                            <div
                                className="shop-card-image-tint-overlay"
                                onClick={cardClickMode === 'toggle' ? (e) => { e.preventDefault(); e.stopPropagation(); setIsImageToggled(prev => !prev); } : undefined}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundColor: cardImgTint,
                                    opacity: activeTintOpacity,
                                    mixBlendMode: cardImgTintBlendMode,
                                    pointerEvents: cardClickMode === 'toggle' ? 'auto' : 'none',
                                    cursor: cardClickMode === 'toggle' ? 'pointer' : undefined,
                                    borderRadius: styleSettings.shopImageInnerRadius || '0px',
                                    transition: `opacity ${cardTransDur} ${cardTransTiming}, background-color ${cardTransDur} ${cardTransTiming}`,
                                    zIndex: 1
                                }}
                            />
                        )}
                        {!isCommercialMode && product.inquiryOnly && showInquiryBadge && (
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: inquiryBadgeBg,
                                color: inquiryBadgeColor,
                                fontSize: inquiryBadgeFontSize,
                                fontWeight: 'bold',
                                padding: '4px 8px',
                                borderRadius: inquiryBadgeRadius,
                                border: inquiryBadgeBorder,
                                zIndex: 2,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                {inquiryBadgeText}
                            </div>
                        )}
                        {!isCommercialMode && cardStock === 0 && (
                            <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#ef4444', color: 'white', fontSize: '0.75rem', fontWeight: 'bold', padding: '4px 8px', borderRadius: '4px', zIndex: 2 }}>
                                Out of Stock
                            </div>
                        )}
                    </Link>
                )}
            </div>

            <div className="shop-product-card-body" style={{ 
                ...contentPaddingStyle,
                display: 'flex', 
                flexDirection: 'column', 
                flex: 1, 
                gap: contentGap,
                background: contentBg,
                alignItems: contentAlign === 'center' ? 'center' : (contentAlign === 'right' ? 'flex-end' : 'flex-start'),
                justifyContent: contentJustify,
                textAlign: contentAlign,
                boxSizing: 'border-box'
            }}>
                <span 
                    className="shop-product-category"
                    style={{ 
                        ...resolveFontStyles('body', { 
                            align: (isMobile && styleSettings.shopProductCategoryMobileAlign) ? styleSettings.shopProductCategoryMobileAlign : (settings.shopProductCategoryAlign || contentAlign), 
                            preset: settings.shopProductCategoryPreset 
                        }),
                        fontFamily: (styleSettings.shopProductCategoryFontFamily && styleSettings.shopProductCategoryFontFamily !== 'inherit') ? styleSettings.shopProductCategoryFontFamily : ((settings.shopProductCategoryFontFamily && settings.shopProductCategoryFontFamily !== 'inherit') ? settings.shopProductCategoryFontFamily : undefined),
                        fontSize: addUnit(styleSettings.shopProductCategorySize !== undefined && styleSettings.shopProductCategorySize !== '' ? styleSettings.shopProductCategorySize : (settings.shopProductCategorySize || '0.8rem')),
                        color: styleSettings.shopProductCategoryColor || settings.shopProductCategoryColor || primaryColor,
                        fontWeight: styleSettings.shopProductCategoryWeight || settings.shopProductCategoryWeight || 'bold', 
                        textTransform: styleSettings.shopProductCategoryTransform || settings.shopProductCategoryTransform || 'uppercase', 
                        display: 'inline-flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        alignSelf: (isMobile && styleSettings.shopProductCategoryMobileAlign)
                            ? (styleSettings.shopProductCategoryMobileAlign === 'center' ? 'center' : (styleSettings.shopProductCategoryMobileAlign === 'right' ? 'flex-end' : 'flex-start'))
                            : undefined,
                        background: cardCatBg,
                        border: cardCatBorder,
                        borderRadius: cardCatRadius,
                        padding: cardCatPadding,
                        order: parseInt(styleSettings.shopCardOrderCategory) || 1
                    }} 
                >
                    {renderCardCategoryContent(isEditing)}
                </span>
                <h3 className="shop-product-title" style={{ 
                    margin: 0, 
                    fontWeight: '700', 
                    ...resolveFontStyles('h3', {
                        align: settings.shopProductNameAlign || contentAlign,
                        preset: settings.shopProductNamePreset
                    }), 
                    fontFamily: (styleSettings.shopProductNameFontFamily && styleSettings.shopProductNameFontFamily !== 'inherit') ? styleSettings.shopProductNameFontFamily : ((settings.shopProductNameFontFamily && settings.shopProductNameFontFamily !== 'inherit') ? settings.shopProductNameFontFamily : undefined),
                    fontSize: addUnit(styleSettings.shopProductNameSize !== undefined && styleSettings.shopProductNameSize !== '' ? styleSettings.shopProductNameSize : (settings.shopProductNameSize || '1.2rem')),
                    color: styleSettings.shopProductNameColor || settings.shopProductNameColor || '#0f172a',
                    order: parseInt(styleSettings.shopCardOrderName) || 2,
                    width: '100%'
                }}>
                    {isEditing ? (
                        <Editable 
                            as="span" 
                            isEditing={true} 
                            value={product.name || ''} 
                            onChange={e => updateProductField && updateProductField(product.id, 'name', e.target.value)} 
                            onStyleChange={(p, v) => update && update(['settings', `shopProductName${p}`], v)}
                            currentStyles={{ 
                                align: settings.shopProductNameAlign, 
                                size: styleSettings.shopProductNameSize || settings.shopProductNameSize, 
                                color: styleSettings.shopProductNameColor || settings.shopProductNameColor, 
                                fontFamily: styleSettings.shopProductNameFontFamily || settings.shopProductNameFontFamily, 
                                preset: settings.shopProductNamePreset 
                            }}
                        />
                    ) : (
                        <Link to={normalizeAppUrl(`/product/${product.slug || product.id}`)} style={{ color: 'inherit', textDecoration: 'none' }}>{stripHtml(product.name)}</Link>
                    )}
                </h3>
                {showDescription && (isEditing ? (
                    <Editable 
                        as="div" 
                        type="textarea" 
                        className="shop-product-desc"
                        isEditing={true} 
                        value={product.shortDescription !== undefined ? product.shortDescription : (product.description ? cleanHtmlDescription(product.description) : '')} 
                        onChange={e => updateProductField && updateProductField(product.id, 'shortDescription', e.target.value)} 
                        onStyleChange={(p, v) => update && update(['settings', `shopProductDesc${p}`], v)}
                        currentStyles={{ 
                            align: styleSettings.shopProductDescAlign || settings.shopProductDescAlign, 
                            size: styleSettings.shopProductDescSize || settings.shopProductDescSize, 
                            color: styleSettings.shopProductDescColor || settings.shopProductDescColor, 
                            fontFamily: styleSettings.shopProductDescFontFamily || settings.shopProductDescFontFamily, 
                            preset: settings.shopProductDescPreset 
                        }}
                        style={{ 
                            ...resolveFontStyles('body', { 
                                align: styleSettings.shopProductDescAlign || settings.shopProductDescAlign || contentAlign, 
                                preset: settings.shopProductDescPreset 
                            }),
                            margin: styleSettings.shopProductDescMargin || '0px', 
                            flex: isSpreadVertical ? 1 : '0 0 auto', 
                            minHeight: isSpreadVertical ? '40px' : undefined,
                            fontFamily: (styleSettings.shopProductDescFontFamily && styleSettings.shopProductDescFontFamily !== 'inherit') ? styleSettings.shopProductDescFontFamily : ((settings.shopProductDescFontFamily && settings.shopProductDescFontFamily !== 'inherit') ? settings.shopProductDescFontFamily : undefined),
                            fontSize: addUnit(styleSettings.shopProductDescSize !== undefined && styleSettings.shopProductDescSize !== '' ? styleSettings.shopProductDescSize : (settings.shopProductDescSize || '0.9rem')),
                            color: styleSettings.shopProductDescColor || settings.shopProductDescColor || '#64748b',
                            fontWeight: styleSettings.shopProductDescWeight || 'normal',
                            lineHeight: styleSettings.shopProductDescLineHeight || '1.5',
                            textTransform: styleSettings.shopProductDescTransform || 'none',
                            textAlign: styleSettings.shopProductDescAlign || settings.shopProductDescAlign || contentAlign,
                            order: parseInt(styleSettings.shopCardOrderDesc) || 3,
                            width: '100%',
                            cursor: 'text'
                        }} 
                    />
                ) : (
                    <p className="shop-product-desc" style={{ 
                        margin: styleSettings.shopProductDescMargin || '0px', 
                        flex: isSpreadVertical ? 1 : '0 0 auto', 
                        display: (styleSettings.shopProductDescLineClamp === 'none' || styleSettings.shopProductDescLineClamp === 'unlimited' || styleSettings.shopProductDescLineClamp === '0') ? 'block' : '-webkit-box', 
                        WebkitLineClamp: (styleSettings.shopProductDescLineClamp === 'none' || styleSettings.shopProductDescLineClamp === 'unlimited' || styleSettings.shopProductDescLineClamp === '0') ? undefined : (parseInt(styleSettings.shopProductDescLineClamp, 10) || 2), 
                        WebkitBoxOrient: (styleSettings.shopProductDescLineClamp === 'none' || styleSettings.shopProductDescLineClamp === 'unlimited' || styleSettings.shopProductDescLineClamp === '0') ? undefined : 'vertical', 
                        overflow: (styleSettings.shopProductDescLineClamp === 'none' || styleSettings.shopProductDescLineClamp === 'unlimited' || styleSettings.shopProductDescLineClamp === '0') ? 'visible' : 'hidden', 
                        ...resolveFontStyles('body', { 
                            align: styleSettings.shopProductDescAlign || settings.shopProductDescAlign || contentAlign, 
                            preset: settings.shopProductDescPreset 
                        }),
                        fontFamily: (styleSettings.shopProductDescFontFamily && styleSettings.shopProductDescFontFamily !== 'inherit') ? styleSettings.shopProductDescFontFamily : ((settings.shopProductDescFontFamily && settings.shopProductDescFontFamily !== 'inherit') ? settings.shopProductDescFontFamily : undefined),
                        fontSize: addUnit(styleSettings.shopProductDescSize !== undefined && styleSettings.shopProductDescSize !== '' ? styleSettings.shopProductDescSize : (settings.shopProductDescSize || '0.9rem')),
                        color: styleSettings.shopProductDescColor || settings.shopProductDescColor || '#64748b',
                        fontWeight: styleSettings.shopProductDescWeight || 'normal',
                        lineHeight: styleSettings.shopProductDescLineHeight || '1.5',
                        textTransform: styleSettings.shopProductDescTransform || 'none',
                        textAlign: styleSettings.shopProductDescAlign || settings.shopProductDescAlign || contentAlign,
                        order: parseInt(styleSettings.shopCardOrderDesc) || 3,
                        width: '100%'
                    }}>{product.shortDescription
                        ? cleanHtmlDescription(product.shortDescription)
                        : cleanHtmlDescription(product.description)}</p>
                ))}
                
                {hasSelectableOptions && (styleSettings.shopCardShowOptions !== false && styleSettings.shopCardShowOptions !== 'false') && (() => {
                    const formatSpacing = (val, fallback = '') => {
                        if (val === undefined || val === null || val === '') return fallback;
                        const s = String(val).trim();
                        if (s.includes(' ')) return s;
                        return addUnit(s);
                    };

                    let optionsOrder = 3.5;
                    const placement = styleSettings.shopCardOptionsPlacement || 'below-desc';
                    if (placement === 'top' || placement === 'above-category') optionsOrder = 0.5;
                    else if (placement === 'above-title') optionsOrder = 1.5;
                    else if (placement === 'below-title' || placement === 'between-title-desc') optionsOrder = 2.5;
                    else if (placement === 'above-desc') optionsOrder = 2.8;
                    else if (placement === 'below-desc') optionsOrder = 3.5;
                    else if (placement === 'above-actions' || placement === 'above-price') optionsOrder = 3.8;
                    else if (placement === 'below-actions' || placement === 'bottom') optionsOrder = 5;
                    if (placement === 'custom' || (styleSettings.shopCardOrderOptions !== undefined && styleSettings.shopCardOrderOptions !== null && styleSettings.shopCardOrderOptions !== '')) {
                        const parsed = parseFloat(styleSettings.shopCardOrderOptions);
                        if (!isNaN(parsed)) optionsOrder = parsed;
                    }

                    const layout = styleSettings.shopCardOptionsLayout || 'column';
                    const labelPos = styleSettings.shopCardOptionsLabelPosition || 'beside';
                    const align = styleSettings.shopCardOptionsAlign || 'stretch';

                    const isGrid = layout === 'grid-2';
                    const isRow = layout === 'row';

                    const containerStyle = {
                        display: isGrid ? 'grid' : 'flex',
                        gridTemplateColumns: isGrid ? 'repeat(2, minmax(0, 1fr))' : undefined,
                        flexDirection: isRow ? 'row' : 'column',
                        flexWrap: isRow ? 'wrap' : 'nowrap',
                        gap: formatSpacing(styleSettings.shopCardOptionsGap, '6px'),
                        width: '100%',
                        margin: formatSpacing(styleSettings.shopCardOptionsMargin, '8px 0 10px 0'),
                        padding: formatSpacing(styleSettings.shopCardOptionsContainerPadding, undefined),
                        background: styleSettings.shopCardOptionsContainerBg || 'transparent',
                        border: styleSettings.shopCardOptionsContainerBorder && styleSettings.shopCardOptionsContainerBorder !== 'none' ? styleSettings.shopCardOptionsContainerBorder : undefined,
                        borderRadius: formatSpacing(styleSettings.shopCardOptionsContainerRadius, undefined),
                        boxShadow: styleSettings.shopCardOptionsContainerShadow && styleSettings.shopCardOptionsContainerShadow !== 'none' ? styleSettings.shopCardOptionsContainerShadow : undefined,
                        order: optionsOrder,
                        alignItems: align === 'center' ? 'center' : (align === 'flex-end' || align === 'right') ? 'flex-end' : (align === 'flex-start' || align === 'left') ? 'flex-start' : 'stretch',
                        boxSizing: 'border-box'
                    };

                    const rowFlexDir = labelPos === 'above' ? 'column' : 'row';
                    const rowAlign = labelPos === 'above' 
                        ? (align === 'center' ? 'center' : (align === 'flex-end' || align === 'right') ? 'flex-end' : 'stretch') 
                        : 'center';
                    const rowJustify = labelPos === 'beside'
                        ? (align === 'center' ? 'center' : (align === 'flex-end' || align === 'right') ? 'flex-end' : 'space-between')
                        : 'flex-start';

                    const widthMode = styleSettings.shopCardOptionsWidthMode || 'flex';
                    const selectMaxWidth = widthMode === 'full' 
                        ? '100%' 
                        : (widthMode === 'auto' 
                            ? 'none' 
                            : (styleSettings.shopCardOptionsMaxWidth ? formatSpacing(styleSettings.shopCardOptionsMaxWidth) : '180px'));
                    const selectWidth = (widthMode === 'full' || labelPos === 'above') ? '100%' : (widthMode === 'auto' ? 'auto' : undefined);
                    const selectFlex = (widthMode === 'full' || widthMode === 'flex') ? 1 : undefined;

                    return (
                        <div 
                            className="shop-card-options-wrapper"
                            onClick={e => e.stopPropagation()}
                            style={containerStyle}
                        >
                            {Object.entries(selectableAttrs).map(([attrName, optionsList]) => (
                                <div 
                                    key={attrName} 
                                    className="shop-card-option-row"
                                    style={{ 
                                        display: 'flex', 
                                        flexDirection: rowFlexDir,
                                        alignItems: rowAlign, 
                                        justifyContent: rowJustify, 
                                        gap: formatSpacing(styleSettings.shopCardOptionsLabelGap, '6px'), 
                                        width: isRow ? 'auto' : '100%',
                                        flex: isRow ? '1 1 auto' : undefined,
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {labelPos !== 'hidden' && (
                                        <span 
                                            className="shop-card-option-label"
                                            style={{ 
                                                fontSize: formatSpacing(styleSettings.shopCardOptionsLabelSize, '0.74rem'), 
                                                fontWeight: styleSettings.shopCardOptionsLabelWeight || '700', 
                                                color: styleSettings.shopCardOptionsLabelColor || '#475569', 
                                                fontFamily: (styleSettings.shopCardOptionsLabelFontFamily && styleSettings.shopCardOptionsLabelFontFamily !== 'inherit') ? styleSettings.shopCardOptionsLabelFontFamily : undefined,
                                                textTransform: styleSettings.shopCardOptionsLabelTransform || 'none',
                                                letterSpacing: styleSettings.shopCardOptionsLabelLetterSpacing ? formatSpacing(styleSettings.shopCardOptionsLabelLetterSpacing) : undefined,
                                                minWidth: styleSettings.shopCardOptionsLabelMinWidth ? formatSpacing(styleSettings.shopCardOptionsLabelMinWidth) : undefined,
                                                textAlign: styleSettings.shopCardOptionsLabelAlign || 'left',
                                                whiteSpace: 'nowrap' 
                                            }}
                                        >
                                            {attrName}:
                                        </span>
                                    )}
                                    <select
                                        className="shop-card-option-select"
                                        value={effectiveOptions[attrName] || optionsList[0]}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            if (handleOptionChange) {
                                                handleOptionChange(pId, attrName, e.target.value);
                                            }
                                        }}
                                        style={{
                                            flex: selectFlex,
                                            width: selectWidth,
                                            maxWidth: selectMaxWidth,
                                            height: styleSettings.shopCardOptionsHeight ? formatSpacing(styleSettings.shopCardOptionsHeight) : undefined,
                                            minHeight: styleSettings.shopCardOptionsMinHeight ? formatSpacing(styleSettings.shopCardOptionsMinHeight) : undefined,
                                            padding: hasGranularOptPad ? undefined : formatSpacing(styleSettings.shopCardOptionsPadding, '4px 8px'),
                                            paddingTop: hasGranularOptPad ? addUnit(optPadTop !== undefined && optPadTop !== '' ? optPadTop : '4px') : undefined,
                                            paddingRight: hasGranularOptPad ? addUnit(optPadRight !== undefined && optPadRight !== '' ? optPadRight : '8px') : undefined,
                                            paddingBottom: hasGranularOptPad ? addUnit(optPadBottom !== undefined && optPadBottom !== '' ? optPadBottom : '4px') : undefined,
                                            paddingLeft: hasGranularOptPad ? addUnit(optPadLeft !== undefined && optPadLeft !== '' ? optPadLeft : '8px') : undefined,
                                            borderRadius: formatSpacing(styleSettings.shopCardOptionsRadius, '6px'),
                                            border: styleSettings.shopCardOptionsBorder || '1px solid #cbd5e1',
                                            fontSize: formatSpacing(styleSettings.shopCardOptionsFontSize, '0.78rem'),
                                            fontWeight: styleSettings.shopCardOptionsFontWeight || '600',
                                            fontFamily: (styleSettings.shopCardOptionsFontFamily && styleSettings.shopCardOptionsFontFamily !== 'inherit') ? styleSettings.shopCardOptionsFontFamily : undefined,
                                            textTransform: styleSettings.shopCardOptionsTextTransform || 'none',
                                            background: styleSettings.shopCardOptionsBg || '#ffffff',
                                            color: styleSettings.shopCardOptionsColor || '#0f172a',
                                            boxShadow: styleSettings.shopCardOptionsShadow && styleSettings.shopCardOptionsShadow !== 'none' ? styleSettings.shopCardOptionsShadow : undefined,
                                            cursor: 'pointer',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {optionsList.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                            {styleSettings.shopCardOptionsCustomCss && (
                                <style dangerouslySetInnerHTML={{ __html: styleSettings.shopCardOptionsCustomCss }} />
                            )}
                        </div>
                    );
                })()}
                
                <div className="shop-product-action-row" style={{ 
                    display: 'flex', 
                    flexDirection: (actionsDir === 'column-center' || actionRowSplit === 'column' || isColumnBtn) ? (actionsDir === 'column-reverse' ? 'column-reverse' : 'column') : actionsDir,
                    justifyContent: (isColumnActions || isColumnBtn) ? 'flex-start' : (actionsAlign || 'space-between'), 
                    alignItems: (actionsDir === 'column-center') ? 'center' : ((isColumnActions || isColumnBtn) ? (styleSettings.shopCardContentAlign === 'center' ? 'center' : (styleSettings.shopCardContentAlign === 'right' ? 'flex-end' : 'stretch')) : 'center'), 
                    marginTop: isSpreadVertical ? 'auto' : '0px',
                    order: parseInt(styleSettings.shopCardOrderActions) || 4,
                    width: '100%',
                    gap: addUnit(
                        (isMobile && styleSettings.shopCardActionRowMobileGap !== undefined && styleSettings.shopCardActionRowMobileGap !== '')
                            ? styleSettings.shopCardActionRowMobileGap
                            : (styleSettings.shopCardActionRowGap || '10px')
                    ),
                    boxSizing: 'border-box'
                }}>
                    {isEditing ? (
                        <div className="shop-product-price" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '2px', 
                            fontWeight: '800', 
                            width: priceColWidth,
                            flex: (actionRowSplit === 'price-auto-buttons-fill') ? '0 0 auto' : undefined,
                            boxSizing: 'border-box',
                            ...resolveFontStyles('body', {
                                align: settings.shopProductPriceAlign || contentAlign,
                                preset: settings.shopProductPricePreset
                            }), 
                            fontFamily: (styleSettings.shopProductPriceFontFamily && styleSettings.shopProductPriceFontFamily !== 'inherit') ? styleSettings.shopProductPriceFontFamily : ((settings.shopProductPriceFontFamily && settings.shopProductPriceFontFamily !== 'inherit') ? settings.shopProductPriceFontFamily : undefined),
                            fontSize: addUnit(styleSettings.shopProductPriceSize !== undefined && styleSettings.shopProductPriceSize !== '' ? styleSettings.shopProductPriceSize : (settings.shopProductPriceSize || '1.25rem')),
                            color: styleSettings.shopProductPriceColor || settings.shopProductPriceColor || '#0f172a'
                        }}>
                            {hasSelectableOptions ? (
                                <span title="Selected variation price (edit in Product Details or Variations Editor)">
                                    {priceInfo.isOnSale && priceInfo.regularPrice ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
                                            <span>{activeFormatPrice ? activeFormatPrice(priceInfo.price) : `$${priceInfo.price.toFixed(2)}`}</span>
                                            <span style={{ fontSize: '0.82em', color: '#94a3b8', textDecoration: 'line-through', fontWeight: '500' }}>
                                                {activeFormatPrice ? activeFormatPrice(priceInfo.regularPrice) : `$${priceInfo.regularPrice.toFixed(2)}`}
                                            </span>
                                        </span>
                                    ) : priceInfo.price > 0 ? (
                                        activeFormatPrice ? activeFormatPrice(priceInfo.price) : `$${priceInfo.price.toFixed(2)}`
                                    ) : (
                                        getProductDisplayPrice(product, activeFormatPrice, callForPriceText)
                                    )}
                                </span>
                            ) : (
                                <>
                                    <span>$</span>
                                    <Editable 
                                        as="span" 
                                        isEditing={true} 
                                        value={String(product.price !== undefined ? product.price : '')} 
                                        onChange={e => updateProductField && updateProductField(product.id, 'price', e.target.value)} 
                                        onStyleChange={(p, v) => update && update(['settings', `shopProductPrice${p}`], v)}
                                        currentStyles={{ 
                                            align: settings.shopProductPriceAlign, 
                                            size: styleSettings.shopProductPriceSize || settings.shopProductPriceSize, 
                                            color: styleSettings.shopProductPriceColor || settings.shopProductPriceColor, 
                                            fontFamily: styleSettings.shopProductPriceFontFamily || settings.shopProductPriceFontFamily, 
                                            preset: settings.shopProductPricePreset 
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    ) : (() => {
                        const effectiveCommercialMode = Boolean(isCommercialMode || styleSettings?.shopCommercialOrderForm || settings?.shopCommercialOrderForm || settings?.cartCommercialMode);
                        if (effectiveCommercialMode) return null;

                        const isCallForPrice = (product.inquiryOnly || getProductDisplayPrice(product, undefined, callForPriceText) === callForPriceText);
                        const hideCallForPrice = Boolean(styleSettings.shopHideCallForPriceText || styleSettings.shopCallForPriceHideText);
                        const priceFontSize = isCallForPrice
                            ? addUnit(styleSettings.shopCallForPriceFontSize || styleSettings.shopPriceFallbackFontSize || styleSettings.shopProductPriceSize || '0.9rem')
                            : addUnit(styleSettings.shopProductPriceSize !== undefined && styleSettings.shopProductPriceSize !== '' ? styleSettings.shopProductPriceSize : (settings.shopProductPriceSize || '1.25rem'));

                        if (isCallForPrice && hideCallForPrice) return null;

                        return (
                            <span className="shop-product-price" style={{ 
                                fontWeight: '800', 
                                width: priceColWidth,
                                flex: (actionRowSplit === 'price-auto-buttons-fill') ? '0 0 auto' : undefined,
                                boxSizing: 'border-box',
                                ...resolveFontStyles('body', {
                                    align: settings.shopProductPriceAlign || contentAlign,
                                    preset: settings.shopProductPricePreset
                                }), 
                                fontFamily: (styleSettings.shopProductPriceFontFamily && styleSettings.shopProductPriceFontFamily !== 'inherit') ? styleSettings.shopProductPriceFontFamily : ((settings.shopProductPriceFontFamily && settings.shopProductPriceFontFamily !== 'inherit') ? settings.shopProductPriceFontFamily : undefined),
                                fontSize: priceFontSize,
                                color: isCallForPrice ? (styleSettings.shopCallForPriceColor || '#d97706') : (styleSettings.shopProductPriceColor || settings.shopProductPriceColor || '#0f172a')
                            }}>
                                {hasSelectableOptions && matchedVar ? (
                                    priceInfo.isOnSale && priceInfo.regularPrice ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
                                            <span>{activeFormatPrice ? activeFormatPrice(priceInfo.price) : `$${priceInfo.price.toFixed(2)}`}</span>
                                            <span style={{ fontSize: '0.82em', color: '#94a3b8', textDecoration: 'line-through', fontWeight: '500' }}>
                                                {activeFormatPrice ? activeFormatPrice(priceInfo.regularPrice) : `$${priceInfo.regularPrice.toFixed(2)}`}
                                            </span>
                                        </span>
                                    ) : priceInfo.price > 0 ? (
                                        activeFormatPrice ? activeFormatPrice(priceInfo.price) : `$${priceInfo.price.toFixed(2)}`
                                    ) : (
                                        getProductDisplayPrice(product, activeFormatPrice, callForPriceText)
                                    )
                                ) : (
                                    getProductDisplayPrice(product, activeFormatPrice, callForPriceText)
                                )}
                            </span>
                        );
                    })()}
                    
                    <div className="shop-card-buttons-wrapper" style={{ 
                        display: 'flex', 
                        flexDirection: isColumnBtn ? ((btnLayout === 'column-reverse') ? 'column-reverse' : 'column') : 'row',
                        gap: addUnit(
                            (isMobile && styleSettings.shopCardButtonsMobileGap)
                                ? styleSettings.shopCardButtonsMobileGap
                                : (styleSettings.shopCardButtonsGap || '8px')
                        ),
                        justifyContent: isColumnBtn ? 'center' : (styleSettings.shopCardButtonsAlign || 'flex-end'),
                        alignItems: isColumnBtn ? 'stretch' : 'center',
                        width: isColumnBtn ? '100%' : buttonsColWidth,
                        flex: isColumnBtn ? '1 1 100%' : buttonsColFlex,
                        boxSizing: 'border-box'
                    }}>
                        {renderCardQtySelector()}
                        {btnLayout === 'cart-only' ? (
                            renderCartBtn()
                        ) : btnLayout === 'details-only' ? (
                            renderDetailsBtn()
                        ) : btnLayout === 'column-reverse' ? (
                            <>
                                {renderCartBtn()}
                                {renderDetailsBtn()}
                            </>
                        ) : (
                            <>
                                {renderDetailsBtn()}
                                {renderCartBtn()}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShopProductCard;
